import { useMutation, useQuery } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';

import { SUPPORT_CHAT_API_URL, SUPPORT_CONVERSATION_QUERY_KEY } from '@/constants';
import { apiUrl } from '@/helpers';
import {
  ErrorResponse,
  SupportChatMessageRequest,
  SupportChatReply,
  SupportConversation,
  SupportEscalationRequest,
  SupportEscalationResult
} from '@/types';

type SupportChatOptions<T> = {
  onSuccess: (response: AxiosResponse<T>) => void;
  onError: (error: ErrorResponse) => void;
};

export const supportChatEndpoints = {
  sendMessage: (payload: SupportChatMessageRequest) =>
    axios.post<SupportChatReply>(`${apiUrl()}${SUPPORT_CHAT_API_URL}/messages`, payload),
  getConversation: (id: number) =>
    axios.get<SupportConversation>(`${apiUrl()}${SUPPORT_CHAT_API_URL}/${id}`),
  escalate: ({ id, payload }: { id: number; payload: SupportEscalationRequest }) =>
    axios.post<SupportEscalationResult>(
      `${apiUrl()}${SUPPORT_CHAT_API_URL}/${id}/escalate`,
      payload
    ),
  rate: ({ id, helpful }: { id: number; helpful: boolean }) =>
    axios.post(`${apiUrl()}${SUPPORT_CHAT_API_URL}/${id}/rating`, { helpful })
};

export const useGetSupportConversation = (
  id: number | null,
  onError: (error: ErrorResponse) => void
) => {
  return useQuery(
    [SUPPORT_CONVERSATION_QUERY_KEY, id],
    () => supportChatEndpoints.getConversation(id as number),
    {
      enabled: !!id,
      retry: false,
      // The panel keeps its own copy of the messages; a cached copy would briefly show an older
      // conversation every time the panel is reopened.
      cacheTime: 0,
      onError: (error) => onError(error as ErrorResponse)
    }
  );
};

export const useSendSupportMessageMutation = (options: SupportChatOptions<SupportChatReply>) => {
  return useMutation(supportChatEndpoints.sendMessage, {
    onSuccess: options.onSuccess,
    onError: (error) => options.onError(error as ErrorResponse)
  });
};

export const useEscalateSupportConversationMutation = (
  options: SupportChatOptions<SupportEscalationResult>
) => {
  return useMutation(supportChatEndpoints.escalate, {
    onSuccess: options.onSuccess,
    onError: (error) => options.onError(error as ErrorResponse)
  });
};

export const useRateSupportConversationMutation = () => {
  return useMutation(supportChatEndpoints.rate);
};
